/* eslint-disable */
import React, { useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Card,
  Center,
  Group,
  Loader as MantineLoader,
  Pagination,
  Switch,
  Table,
  Text,
  TextInput,
  createStyles,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useGetScopedUsersList } from "api/users/getScopedUsersList";
import { useUpdateUserNotificationPreferences } from "api/users/updateUserNotificationPreferences";

const useStyles = createStyles((theme) => ({
  card: {
    width: "100%",
    padding: 18,
    boxShadow: theme.shadows.sm,
    borderRadius: theme.radius.md,
  },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  subtitle: { fontSize: 12, color: theme.colors.gray[6] },
  thead: { backgroundColor: "#f5f6f7" },
  employeeCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  empMeta: { lineHeight: 1 },
  empName: { fontWeight: 600, fontSize: 13 },
  empEmail: { fontSize: 13, color: theme.colors.gray[6] },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 14,
  },
  footerText: { fontSize: 13, color: theme.colors.gray[6] },
}));

const getRoleBadgeColor = (role?: string): string => {
  const colorMap: Record<string, string> = {
    superadmin: "red",
    admin: "blue",
    supervisor: "grape",
    member: "gray",
    director: "cyan",
    band_administrator: "teal",
  };
  return colorMap[(role || "").toLowerCase()] || "gray";
};

export default function NotificationSettingsPage() {
  const { classes } = useStyles();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isFetching } = useGetScopedUsersList(page, limit, search);
  const { mutate: updatePrefs, isLoading: saving } =
    useUpdateUserNotificationPreferences();

  const users = data?.data ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination?.pages ?? 1;

  const visible = useMemo(() => users, [users]);

  return (
    <Card className={classes.card}>
      <Group position="apart" mb="md">
        <Group spacing="xs">
          <Text className={classes.title}>Notification Settings</Text>
          <Text className={classes.subtitle}>
            Manage email and phone notification preferences per user
          </Text>
        </Group>

        <TextInput
          placeholder="Search by name or email"
          icon={<IconSearch size={16} />}
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          style={{ width: 260 }}
        />
      </Group>

      {isFetching && (
        <Group position="center" py="xs" spacing="xs">
          <MantineLoader size="sm" />
          <Text size="sm" color="dimmed">
            Loading…
          </Text>
        </Group>
      )}

      <Table verticalSpacing="sm" highlightOnHover>
        <thead className={classes.thead}>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Email notifications</th>
            <th>Phone notifications</th>
          </tr>
        </thead>
        <tbody>
          {visible.length === 0 ? (
            <tr>
              <td colSpan={4}>
                <Center>No users found.</Center>
              </td>
            </tr>
          ) : (
            visible.map((u: any) => {
              const name = `${u?.firstName || ""} ${u?.lastName || ""}`.trim();
              const initials = name
                .split(" ")
                .filter(Boolean)
                .map((p: string) => p[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();

              const emailEnabled = u?.notificationPreferences?.email ?? true;
              const phoneEnabled = u?.notificationPreferences?.phone ?? true;

              return (
                <tr key={u._id}>
                  <td>
                    <div className={classes.employeeCell}>
                      <Avatar color="gray" radius={40} size={40}>
                        {initials || "U"}
                      </Avatar>
                      <div className={classes.empMeta}>
                        <div className={classes.empName}>{name || "—"}</div>
                        <Text size="sm" className={classes.empEmail}>
                          {u?.email || "—"}
                        </Text>
                      </div>
                    </div>
                  </td>
                  <td>
                    <Badge
                      color={getRoleBadgeColor(u?.role)}
                      variant="light"
                    >
                      {u?.role || "N/A"}
                    </Badge>
                  </td>
                  <td>
                    <Switch
                      checked={emailEnabled}
                      disabled={saving}
                      onChange={(e) =>
                        updatePrefs({
                          userId: u._id,
                          preferences: { email: e.currentTarget.checked },
                        })
                      }
                      label={emailEnabled ? "Enabled" : "Disabled"}
                    />
                  </td>
                  <td>
                    <Switch
                      checked={phoneEnabled}
                      disabled={saving}
                      onChange={(e) =>
                        updatePrefs({
                          userId: u._id,
                          preferences: { phone: e.currentTarget.checked },
                        })
                      }
                      label={phoneEnabled ? "Enabled" : "Disabled"}
                    />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      <div className={classes.footer}>
        <Text className={classes.footerText}>
          Showing {visible.length} user{visible.length === 1 ? "" : "s"}
        </Text>
        <Pagination
          total={totalPages}
          page={page}
          onChange={setPage}
          siblings={1}
          radius="sm"
        />
      </div>
    </Card>
  );
}

